import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CoachNode } from 'src/generated/webapp_gql';

import { getMyCoaches } from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType } from 'src/modules/user/models/user';
import chevronIcon from 'src/public/img/icon-chevron.svg';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import { color, font } from 'src/shared/styles/variables';
import { getSessionUtilizationInfo } from '../selectors/videoSessionsSelectors';

import CirclesIllustration from 'src/public/img/circles.png';
import CoachIllustration from 'src/public/img/coach.png';
import LowRatingIllustration from 'src/public/img/low-rating.png';
import TherapistIllustration from 'src/public/img/therapist.png';

const Illustration = styled.img({
    width: 224,
    height: 'auto',
    marginBottom: 45,
});

const PageTitle = styled.h2({
    marginBottom: 12,
    color: color.pickledBluewood,
    lineHeight: '40px',
    textAlign: 'center',
    ...font.black(font.size.extraLarge),
});

const PageDescription = styled.p({
    marginBottom: 25,
    color: color.blueBayoux,
    fontSize: font.size.medium,
    lineHeight: '35px',
});

const CardWrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '16px 24px 15px 26px',
    borderRadius: 5,
    marginBottom: 12,
    backgroundColor: color.white,
    border: `1.5px solid ${color.catskillWhite}`,
    cursor: 'pointer',
    '&:last-of-type': {
        marginBottom: 40,
    },
});

const CardContent = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginRight: 20,
});

const CardImage = styled.img({
    width: 62,
    height: 'auto',
    marginRight: 24,
});

const CardTextWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    flexFlow: 'column',
    alignItems: 'flex-start',
});

const CardTitle = styled.p({
    color: color.pickledBluewood,
    ...font.heavy(font.size.medium),
});

const CardDesc = styled.p({
    color: '#4D6580',
});

const ChevronIcon = styled.img({
    width: 12,
    height: 'auto',
});

export enum SessionLowRatingStepTestID {
    CloseButton = 'SessionLowRatingStepTestID.CloseButton',
    Program = 'SessionLowRatingStepTestID.Program',
    ChangeCoach = 'SessionLowRatingStepTestID.ChangeCoach',
    ChangeTherapist = 'SessionLowRatingStepTestID.ChangeTherapist',
    BookCoachingSession = 'SessionLowRatingStepTestID.BookCoachingSession',
    AddCoach = 'SessionLowRatingStepTestID.AddCoach',
    CirclesTile = 'SessionLowRatingStepTestID.CirclesTile',
}

interface ActionCardProps {
    image: string;
    title: string;
    desc?: string;
    testid?: string;
    capitalize?: boolean;
    onClick: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
    image,
    title,
    desc,
    onClick,
    testid,
    capitalize,
}) => {
    const { t } = useTranslation();

    return (
        <CardWrapper
            onClick={onClick}
            data-testid={testid ? testid : SessionLowRatingStepTestID.Program}
        >
            <CardContent>
                <CardImage src={image} data-testid="card_image" />

                <CardTextWrapper>
                    <CardTitle style={{ textTransform: capitalize ? 'capitalize' : 'none' }}>
                        {title}
                    </CardTitle>

                    <CardDesc>
                        {desc ? desc : t('onboarding:your_on_your_way_screen.program')}
                    </CardDesc>
                </CardTextWrapper>
            </CardContent>
            <ChevronIcon src={chevronIcon} />
        </CardWrapper>
    );
};

export interface LowRatingStepProps {
    provider: CoachNode;
}

export const SessionLowRatingStep: React.FC<LowRatingStepProps> = ({ provider }) => {
    const { t } = useTranslation();
    const [localizedVersion] = useLocalizedVersion();
    const history = useHistory();

    const sessionsInfo = useSelector(getSessionUtilizationInfo);
    const myCoaches = useSelector(getMyCoaches);
    const isTherapySession = provider.providerType === ProviderType.THERAPIST;
    const shouldShowSelectNewTherapistButton = isTherapySession;
    const shouldShowSelectNewCoachButton = !isTherapySession;

    const handleSelectNewProviderClick = (providerType: ProviderType) => {
        if (providerType === ProviderType.THERAPIST) {
            return history.replace('/work-with-a-therapist', {
                therapistToRemoveUuid: provider.uuid,
            });
        }

        history.replace('/work-with-a-coach', {
            coachToRemoveUuid: myCoaches.length > 1 ? provider.uuid : undefined,
        });
    };

    const handleBookCoachingSessionClick = () => {
        if (myCoaches.length < 2) {
            history.replace('/schedule-session/calendar', {
                coachUuid: myCoaches[0].uuid,
            });
        } else {
            history.replace('/schedule-session/select-coach');
        }
    };

    return (
        <React.Fragment>
            <Illustration src={LowRatingIllustration} />

            <PageTitle>{t('videoSessions:rate_session.session_low_rating_screen.title')}</PageTitle>
            <PageDescription>
                {t(
                    `videoSessions:rate_session.session_low_rating_screen.description_${
                        isTherapySession ? 'therapy' : 'coaching'
                    }`,
                )}
            </PageDescription>

            {!isTherapySession && !localizedVersion && (
                <ActionCard
                    title={t('onboarding:your_on_your_way_screen.circles.title')}
                    image={CirclesIllustration}
                    desc={t('onboarding:your_on_your_way_screen.circles.description')}
                    onClick={() =>
                        window.open(
                            'https://circles.modernhealth.com/?utm_source=modern-health&utm_medium=web-app',
                            '_blank',
                        )
                    }
                    testid={SessionLowRatingStepTestID.CirclesTile}
                />
            )}

            {shouldShowSelectNewTherapistButton && (
                <>
                    {myCoaches.length && sessionsInfo.coachingVisitsRemaining >= 3 ? (
                        <ActionCard
                            title={t(
                                'videoSessions:rate_session.session_low_rating_screen.book_coaching_session.title',
                            )}
                            desc={t(
                                'videoSessions:rate_session.session_low_rating_screen.book_coaching_session.desc',
                            )}
                            image={CoachIllustration}
                            onClick={handleBookCoachingSessionClick}
                            testid={SessionLowRatingStepTestID.BookCoachingSession}
                        />
                    ) : (
                        <ActionCard
                            title={t(
                                'videoSessions:rate_session.session_low_rating_screen.add_coach.title',
                            )}
                            desc={t(
                                'videoSessions:rate_session.session_low_rating_screen.add_coach.desc',
                            )}
                            image={CoachIllustration}
                            onClick={() => {
                                history.replace('/work-with-a-coach');
                            }}
                            testid={SessionLowRatingStepTestID.AddCoach}
                        />
                    )}

                    <ActionCard
                        title={t(
                            'videoSessions:rate_session.session_low_rating_screen.change_therapist',
                        )}
                        desc={t(
                            'videoSessions:rate_session.session_low_rating_screen.change_provider_desc',
                        )}
                        image={TherapistIllustration}
                        onClick={() => handleSelectNewProviderClick(ProviderType.THERAPIST)}
                        testid={SessionLowRatingStepTestID.ChangeTherapist}
                    />
                </>
            )}

            {shouldShowSelectNewCoachButton && (
                <ActionCard
                    title={t('videoSessions:rate_session.session_low_rating_screen.change_coach')}
                    desc={t(
                        'videoSessions:rate_session.session_low_rating_screen.change_provider_desc',
                    )}
                    image={CoachIllustration}
                    onClick={() => handleSelectNewProviderClick(ProviderType.COACH)}
                    testid={SessionLowRatingStepTestID.ChangeCoach}
                />
            )}
        </React.Fragment>
    );
};
